import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { ArrowRight, Calendar, Tag } from "lucide-react";

export function BlogList() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("");
  useEffect(() => {
    const params = {}; if (cat) params.category = cat;
    http.get("/blog", { params }).then(r => setItems(r.data));
  }, [cat]);
  const cats = [...new Set(items.map(i => i.category).filter(Boolean))];
  const site = window.location.origin;
  useSeo({
    title: "MediPats Blog — Guides on Medical Travel to India",
    description: "Practical guides for international patients travelling to India for medical treatment — visas, costs, hospitals, recovery journeys and more.",
    canonical: `${site}/blog`,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Blog", "name": "MediPats Blog",
      "url": `${site}/blog`,
      "blogPost": items.map(p => ({
        "@type": "BlogPosting", "headline": p.title, "url": `${site}/blog/${p.slug}`,
        "image": p.featured_image, "datePublished": p.published_at, "author": p.author
      }))
    }
  });
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <div className="overline mb-3">Blog & Insights</div>
      <h1 className="font-serif text-4xl md:text-6xl text-[#F3E9EC]">Guides for your medical journey to India</h1>
      <p className="mt-4 text-[#F3E9EC]/70 max-w-2xl">Written by our care team — visa guidance, treatment overviews and honest cost breakdowns for international patients.</p>
      {cats.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-2">
          <button onClick={()=>setCat("")} className={`text-xs px-4 py-2 rounded-full border transition-colors ${cat===""?"bg-[#B47A9A] border-[#B47A9A] text-[#1A0F1B]":"border-[#5E3A5C]/40 text-[#F3E9EC]/70 hover:text-white"}`} data-testid="blog-cat-all">All</button>
          {cats.map(c => (
            <button key={c} onClick={()=>setCat(c)} className={`text-xs px-4 py-2 rounded-full border transition-colors ${cat===c?"bg-[#B47A9A] border-[#B47A9A] text-[#1A0F1B]":"border-[#5E3A5C]/40 text-[#F3E9EC]/70 hover:text-white"}`} data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g,'-')}`}>{c}</button>
          ))}
        </div>
      )}
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(p => (
          <Link to={`/blog/${p.slug}`} key={p.id} className="mp-card overflow-hidden block" data-testid={`blog-card-${p.slug}`}>
            {p.featured_image && <div className="h-48"><img src={p.featured_image} alt={p.title} className="w-full h-full object-cover"/></div>}
            <div className="p-6">
              <span className="text-xs font-mono uppercase tracking-widest text-[#B47A9A]">{p.category}</span>
              <h3 className="font-serif text-xl text-[#F3E9EC] mt-2 leading-snug">{p.title}</h3>
              <p className="mt-3 text-sm text-[#F3E9EC]/60 line-clamp-3">{p.excerpt}</p>
              <div className="mt-5 text-[#B47A9A] text-sm flex items-center gap-2">Read article <ArrowRight className="w-3.5 h-3.5"/></div>
            </div>
          </Link>
        ))}
      </div>
      {items.length === 0 && <p className="text-[#F3E9EC]/50 mt-12">No articles published yet.</p>}
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => { http.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(()=>setPost(false)); }, [slug]);
  const site = typeof window !== "undefined" ? window.location.origin : "";
  const jsonLd = post ? {
    "@context": "https://schema.org", "@type": "Article",
    "headline": post.title, "description": post.meta_description || post.excerpt,
    "image": post.featured_image, "author": { "@type": "Organization", "name": post.author || "MediPats Care Team" },
    "publisher": { "@type": "Organization", "name": "MediPats", "logo": { "@type": "ImageObject", "url": `${site}/logo.png` } },
    "datePublished": post.published_at, "dateModified": post.updated_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${site}/blog/${post.slug}` }
  } : null;
  const faqLd = post && post.faqs && post.faqs.length > 0 ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": post.faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
  } : null;
  useSeo({
    title: post ? (post.seo_title || `${post.title} | MediPats`) : "Loading… | MediPats",
    description: post ? (post.meta_description || post.excerpt) : undefined,
    canonical: post ? (post.canonical || `${site}/blog/${post.slug}`) : undefined,
    image: post ? (post.og_image || post.featured_image) : undefined,
    type: "article",
    jsonLd: faqLd ? [jsonLd, faqLd] : jsonLd
  });
  if (post === null) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#F3E9EC]/60">Loading…</div>;
  if (post === false) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#F3E9EC]/60">Article not found. <Link to="/blog" className="text-[#B47A9A]">Back to blog</Link></div>;
  return (
    <article className="max-w-3xl mx-auto px-6 py-16" data-testid={`blog-detail-${post.slug}`}>
      <div className="overline flex items-center gap-3">
        <span>{post.category}</span>
        {post.published_at && <><span className="w-1 h-1 rounded-full bg-[#B47A9A]"/><span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>{new Date(post.published_at).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"})}</span></>}
      </div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#F3E9EC] mt-4 leading-tight">{post.title}</h1>
      {post.excerpt && <p className="mt-4 text-[#F3E9EC]/70 text-lg leading-relaxed">{post.excerpt}</p>}
      {post.featured_image && (
        <div className="mt-8 rounded-2xl overflow-hidden border border-[#5E3A5C]/30">
          <img src={post.featured_image} alt={post.title} className="w-full h-auto"/>
        </div>
      )}
      <div className="mt-10 prose-mp text-[#F3E9EC]/85" dangerouslySetInnerHTML={{ __html: post.content }}/>
      {post.tags && post.tags.length > 0 && (
        <div className="mt-10 flex items-center gap-2 flex-wrap"><Tag className="w-4 h-4 text-[#B47A9A]"/>{post.tags.map(t=><span key={t} className="text-xs px-3 py-1 rounded-full border border-[#5E3A5C]/60 text-[#F3E9EC]/75">{t}</span>)}</div>
      )}
      {post.faqs && post.faqs.length > 0 && (
        <div className="mt-12">
          <h2 className="font-serif text-2xl text-[#F3E9EC] mb-4">Frequently Asked Questions</h2>
          <div className="space-y-3">{post.faqs.map((f,i)=>(
            <details key={i} className="mp-card p-5 group">
              <summary className="font-serif text-lg text-[#F3E9EC] cursor-pointer">{f.q}</summary>
              <p className="mt-3 text-sm text-[#F3E9EC]/70 leading-relaxed">{f.a}</p>
            </details>))}</div>
        </div>
      )}
      <div className="mt-14 mp-card p-8 text-center">
        <h3 className="font-serif text-2xl text-[#F3E9EC]">Ready to plan your treatment in India?</h3>
        <p className="mt-2 text-sm text-[#F3E9EC]/70">Share a few details and our care coordinators will get back within 24 hours.</p>
        <Link to="/enquiry" className="btn-primary px-6 py-3 mt-6 inline-block" data-testid="blog-enquiry-cta">Get Personalised Cost</Link>
      </div>
    </article>
  );
}
